// deps
import React, { memo, useState, useMemo, useEffect } from "react";

// components
import SplitBillForm from "./SplitBillForm";
import SplitBillUpiStep from "./SplitBillUpiStep";
import SplitBillStatusView, { SplitBillStatusShimmer } from "./SplitBillStatusView";

// interfaces
import { SplitMethod, GuestSplitsMap, ISplitBillHalfCardProps } from "../../../../interfaces";

// constants
import { SplitBillStep, SPLIT_BILL_SUCCESS_OUTCOME, SPLIT_BILL_ERROR_OUTCOME } from "../../../../constants";

// utils
import { initializeGuestSplits } from "./splitBillHelpers";

// hooks
import { useSplitBillHandlers } from "./useSplitBillHandlers";

const SplitBillHalfCardContent: React.FC<ISplitBillHalfCardProps> = ({
    billAmount,
    splitBillContacts,
    splitModeOptions,
    dineCashSplit,
    defaultSplitModeSelection,
    onConfirm,
    handleClose,
    onAddMoreGuests,
    splitBillLoading = false,
    splitBillSuccess = false,
    splitBillSuccessData = null,
    splitBillError = "",
    onRetrySplitBill,
    orderId = "",
    restaurantId = "",
    onKeyboardStateChange,
}) => {
    const [currentStep, setCurrentStep] = useState<SplitBillStep>(SplitBillStep.SPLIT_CONFIGURATION);
    const [selectedTab, setSelectedTab] = useState<SplitMethod>(
        (defaultSplitModeSelection as SplitMethod) || SplitMethod.AMOUNT,
    );

    const orderedContacts = useMemo(() => [splitBillContacts.self, ...splitBillContacts.guests], [splitBillContacts]);

    const initialGuestSplits = useMemo(
        () => initializeGuestSplits(orderedContacts, billAmount),
        [orderedContacts, billAmount],
    );

    const [guestSplits, setGuestSplits] = useState<GuestSplitsMap>(initialGuestSplits);
    const [validationError, setValidationError] = useState<string>("");

    useEffect(() => {
        setGuestSplits(initialGuestSplits);
    }, [initialGuestSplits]);

    useEffect(() => {
        const prev = window.keyboardStateChanged;
        window.keyboardStateChanged = (visible: boolean, keyboardHeight: number): void => {
            onKeyboardStateChange?.(visible ? keyboardHeight : 0);
            prev?.(visible, keyboardHeight);
        };
        return (): void => {
            window.keyboardStateChanged = prev;
            onKeyboardStateChange?.(0);
        };
    }, [onKeyboardStateChange]);

    const {
        confirmedSplitData,
        isFormValid,
        handleTabChange,
        handleSplitEqually,
        handlePercentageChange,
        handleAmountChange,
        handleInputBlur,
        handleShareIncrement,
        handleShareDecrement,
        handleConfirm,
        handleDineCashSelectionChange,
        handleSendRequest,
        handleSuccessDone,
        handleAddMoreGuests,
    } = useSplitBillHandlers({
        billAmount,
        splitModeOptions,
        dineCashSplit,
        orderedContacts,
        onConfirm,
        handleClose,
        onAddMoreGuests,
        orderId,
        restaurantId,
        splitBillError,
        splitBillSuccess,
        guestSplits,
        setGuestSplits,
        selectedTab,
        setSelectedTab,
        setValidationError,
        setCurrentStep,
    });

    if (splitBillLoading) {
        return <SplitBillStatusShimmer />;
    }

    if (splitBillSuccess) {
        return (
            <SplitBillStatusView
                {...SPLIT_BILL_SUCCESS_OUTCOME}
                title={splitBillSuccessData?.statusTitle ?? SPLIT_BILL_SUCCESS_OUTCOME.title}
                subTitle={splitBillSuccessData?.statusMessage ?? SPLIT_BILL_SUCCESS_OUTCOME.subTitle}
                warningCallout={splitBillSuccessData?.warningCallout}
                onCta={handleSuccessDone}
            />
        );
    }

    if (splitBillError) {
        return <SplitBillStatusView {...SPLIT_BILL_ERROR_OUTCOME} onCta={() => onRetrySplitBill?.()} />;
    }

    if (currentStep === SplitBillStep.UPI_ENTRY) {
        return (
            <SplitBillUpiStep
                onSendRequest={handleSendRequest}
                orderId={orderId}
                totalBillAmount={billAmount}
                splitMethod={confirmedSplitData?.method || ""}
                restaurantId={restaurantId}
            />
        );
    }

    return (
        <SplitBillForm
            billAmount={billAmount}
            splitBillContacts={splitBillContacts}
            splitModeOptions={splitModeOptions}
            dineCashSplit={dineCashSplit}
            selectedTab={selectedTab}
            guestSplits={guestSplits}
            validationError={validationError}
            isFormValid={isFormValid}
            onTabChange={handleTabChange}
            onSplitEqually={handleSplitEqually}
            onConfirm={handleConfirm}
            onDineCashSelectionChange={handleDineCashSelectionChange}
            onValidationDismiss={() => setValidationError("")}
            onAddMoreGuests={handleAddMoreGuests}
            onPercentageChange={handlePercentageChange}
            onAmountChange={handleAmountChange}
            onInputBlur={handleInputBlur}
            onShareIncrement={handleShareIncrement}
            onShareDecrement={handleShareDecrement}
        />
    );
};

export default memo(SplitBillHalfCardContent);
